import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import InventoryPanel from "./InventoryPanel";
import CustomerQueue from "./CustomerQueue";
import HeaderBar from "./HeaderBar";
import QuestDisplay from "./QuestDisplay";
import { useGameStateContext } from "../GameStateProvider";
import ActiveChallengeBar from "./ActiveChallengeBar";
import DailyBlendBanner from "./DailyBlendBanner";
import TrendingDrinkBanner from "./TrendingDrinkBanner";
import { CONSTANTS } from "../data";
import BrewingGrid from "./game_screen/BrewingGrid";
import PanelToggleButton from "./game_screen/PanelToggleButton";

const calculateGridDimensions = (containerWidth: number, containerHeight: number) => {
  const isMobile = window.innerWidth < CONSTANTS.MOBILE_UI.MOBILE_BREAKPOINT;
  const isSmallMobile = window.innerWidth < CONSTANTS.MOBILE_UI.SMALL_MOBILE_BREAKPOINT;
  const isLandscapeMobile = window.innerHeight < CONSTANTS.MOBILE_UI.LANDSCAPE_MOBILE_HEIGHT && window.innerWidth > window.innerHeight;

  if (isMobile) {
    let tileSize, gridCols, gridRows, padding;
    if (isLandscapeMobile) {
      tileSize = CONSTANTS.GRID_SYSTEM.LANDSCAPE_MOBILE_TILE_SIZE;
      gridCols = Math.max(30, Math.floor(containerWidth / tileSize));
      gridRows = Math.max(12, Math.floor(containerHeight / tileSize));
      padding = CONSTANTS.GRID_SYSTEM.MIN_PADDING;
    } else if (isSmallMobile) {
      tileSize = CONSTANTS.GRID_SYSTEM.SMALL_MOBILE_TILE_SIZE;
      gridCols = CONSTANTS.GRID_SYSTEM.MOBILE_GRID_COLS;
      gridRows = CONSTANTS.GRID_SYSTEM.MOBILE_GRID_ROWS;
      padding = CONSTANTS.GRID_SYSTEM.MOBILE_PADDING;
    } else {
      tileSize = CONSTANTS.GRID_SYSTEM.MOBILE_TILE_SIZE;
      gridCols = CONSTANTS.GRID_SYSTEM.MOBILE_GRID_COLS;
      gridRows = CONSTANTS.GRID_SYSTEM.MOBILE_GRID_ROWS;
      padding = CONSTANTS.GRID_SYSTEM.MOBILE_PADDING;
    }
    return {
      tileSize, gridCols, gridRows, padding,
      totalWidth: padding * 2 + gridCols * tileSize,
      totalHeight: padding * 2 + gridRows * tileSize,
    };
  }

  const { MIN_TILE_SIZE, MAX_TILE_SIZE, PREFERRED_TILE_SIZE, MIN_GRID_COLS, MAX_GRID_COLS, MIN_GRID_ROWS, MAX_GRID_ROWS, PREFERRED_GRID_COLS, PREFERRED_GRID_ROWS, PADDING_RATIO, MIN_PADDING, MAX_PADDING } = CONSTANTS.GRID_SYSTEM;
  const calculatedPadding = Math.max(MIN_PADDING, Math.min(MAX_PADDING, Math.floor(Math.min(containerWidth, containerHeight) * PADDING_RATIO)));
  const availableWidth = containerWidth - (calculatedPadding * 2);
  const availableHeight = containerHeight - (calculatedPadding * 2);
  let tileSize = PREFERRED_TILE_SIZE;
  let gridCols = PREFERRED_GRID_COLS;
  let gridRows = PREFERRED_GRID_ROWS;

  if (availableWidth < gridCols * tileSize || availableHeight < gridRows * tileSize) {
    const maxTileSizeByWidth = Math.floor(availableWidth / MIN_GRID_COLS);
    const maxTileSizeByHeight = Math.floor(availableHeight / MIN_GRID_ROWS);
    tileSize = Math.max(MIN_TILE_SIZE, Math.min(MAX_TILE_SIZE, Math.min(maxTileSizeByWidth, maxTileSizeByHeight)));
    gridCols = Math.max(MIN_GRID_COLS, Math.min(MAX_GRID_COLS, Math.floor(availableWidth / tileSize)));
    gridRows = Math.max(MIN_GRID_ROWS, Math.min(MAX_GRID_ROWS, Math.floor(availableHeight / tileSize)));
  }

  return {
    tileSize, gridCols, gridRows, padding: calculatedPadding,
    totalWidth: calculatedPadding * 2 + gridCols * tileSize,
    totalHeight: calculatedPadding * 2 + gridRows * tileSize,
  };
};

const getDefaultGridPosition = (stationIndex: number, totalStations: number, gridDimensions: any) => {
  const { gridCols, gridRows } = gridDimensions;
  const { STATION_SPACING_RATIO, CENTER_BIAS } = CONSTANTS.GRID_SYSTEM;
  const centerX = Math.floor(gridCols / 2);
  const centerY = Math.floor(gridRows / 2);
  if (totalStations === 1) {
    return { tileX: centerX, tileY: centerY };
  }
  const spacing = Math.max(1, Math.floor(gridCols * STATION_SPACING_RATIO));
  const totalWidth = (totalStations - 1) * spacing;
  const startX = Math.max(0, Math.min(gridCols - 1, centerX - Math.floor(totalWidth / 2)));
  const biasedY = Math.floor(centerY + (Math.random() - 0.5) * (1 - CENTER_BIAS) * gridRows * 0.3);
  const clampedY = Math.max(0, Math.min(gridRows - 1, biasedY));
  const stationX = Math.max(0, Math.min(gridCols - 1, startX + stationIndex * spacing));
  return { tileX: stationX, tileY: clampedY };
};

const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if (CONSTANTS.TOUCH_INTERACTION.HAPTIC_FEEDBACK_ENABLED && 'vibrate' in navigator) {
    const patterns = { light: [10], medium: [20], heavy: [30] };
    navigator.vibrate(patterns[type]);
  }
};

const GameScreen: React.FC = () => {
  const { state, dispatch } = useGameStateContext();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [touchFeedback, setTouchFeedback] = useState<'inventory' | 'queue' | null>(null);
  const [gridDimensions, setGridDimensions] = useState(() =>
    calculateGridDimensions(window.innerWidth, window.innerHeight - 200)
  );

  const inventoryPanelRef = useRef<HTMLDivElement>(null);
  const customerQueueRef = useRef<HTMLDivElement>(null);
  const [inventoryPanelWidth, setInventoryPanelWidth] = useState(0);
  const [customerQueueWidth, setCustomerQueueWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      if (constraintsRef.current) {
        const rect = constraintsRef.current.getBoundingClientRect();
        const newDimensions = calculateGridDimensions(rect.width, rect.height);
        setGridDimensions(newDimensions);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === inventoryPanelRef.current) {
          setInventoryPanelWidth(entry.contentRect.width);
        }
        if (entry.target === customerQueueRef.current) {
          setCustomerQueueWidth(entry.contentRect.width);
        }
      }
    });
    if (inventoryPanelRef.current) observer.observe(inventoryPanelRef.current);
    if (customerQueueRef.current) observer.observe(customerQueueRef.current);
    return () => observer.disconnect();
  }, [state.inventoryPanelOpen, state.customerQueueOpen]);

  useEffect(() => {
    const updateGridDimensions = () => {
      if (constraintsRef.current) {
        const rect = constraintsRef.current.getBoundingClientRect();
        const newDimensions = calculateGridDimensions(rect.width, rect.height);
        setGridDimensions(newDimensions);
      }
    };
    updateGridDimensions();
    const observer = new ResizeObserver(updateGridDimensions);
    if (constraintsRef.current) observer.observe(constraintsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    state.brewStations.forEach((station, index) => {
      if (station.gridPosition.tileX === -1 && station.gridPosition.tileY === -1) {
        const defaultPosition = getDefaultGridPosition(index, state.brewStations.length, gridDimensions);
        dispatch({
          type: 'UPDATE_BREW_STATION_GRID_POSITION',
          stationIndex: index,
          gridPosition: defaultPosition
        });
      }
    });
  }, [state.brewStations, dispatch, gridDimensions]);

  const handleToggleInventory = () => {
    dispatch({ type: 'TOGGLE_INVENTORY_PANEL' });
    triggerHapticFeedback('medium');
    setTouchFeedback('inventory');
    setTimeout(() => setTouchFeedback(null), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  };

  const handleToggleQueue = () => {
    dispatch({ type: 'TOGGLE_CUSTOMER_QUEUE_PANEL' });
    triggerHapticFeedback('medium');
    setTouchFeedback('queue');
    setTimeout(() => setTouchFeedback(null), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  };

  const panelTransition = { type: "tween", ease: "easeInOut", duration: 0.3 };

  return (
    <div className="h-full bg-bg-main flex flex-col overflow-hidden">
      <HeaderBar />
      <DailyBlendBanner
        dailyBlend={state.dailyBlend}
        seededSession={state.seededSession}
      />
      <TrendingDrinkBanner trendingDrink={state.trendingDrink} />
      {state.masterChefChallenge?.active && (
        <ActiveChallengeBar masterChefChallenge={state.masterChefChallenge} />
      )}
      <div className="bg-surface p-1 border-b border-border-color">
        <QuestDisplay />
      </div>
      <main className="flex-1 flex relative overflow-hidden">
        <AnimatePresence>
          {state.inventoryPanelOpen && (
            <motion.div
              ref={inventoryPanelRef}
              key="inventory-panel"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={panelTransition}
              className="absolute top-0 left-0 h-full z-20 w-2/5 max-w-[256px] sm:w-1/3 sm:max-w-[288px] bg-surface shadow-lg border-r border-border-color"
              style={{ touchAction: 'pan-y' }}
            >
              <InventoryPanel />
            </motion.div>
          )}
        </AnimatePresence>

        <BrewingGrid
          constraintsRef={constraintsRef}
          gridDimensions={gridDimensions}
          brewStations={state.brewStations}
          isLargeScreen={isLargeScreen}
          inventoryPanelWidth={inventoryPanelWidth}
          customerQueueWidth={customerQueueWidth}
          inventoryPanelOpen={state.inventoryPanelOpen}
          customerQueueOpen={state.customerQueueOpen}
        />

        <AnimatePresence>
          {state.customerQueueOpen && (
            <motion.div
              ref={customerQueueRef}
              key="customer-queue"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={panelTransition}
              className="absolute top-0 right-0 h-full z-20 w-2/5 max-w-[256px] sm:w-1/3 sm:max-w-[288px] bg-surface shadow-lg border-l border-border-color"
              style={{ touchAction: 'pan-y' }}
            >
              <CustomerQueue />
            </motion.div>
          )}
        </AnimatePresence>

        <PanelToggleButton
          side="left"
          isOpen={state.inventoryPanelOpen}
          onToggle={handleToggleInventory}
          panelWidth={inventoryPanelWidth}
          isLargeScreen={isLargeScreen}
          touchFeedback={touchFeedback === 'inventory'}
        />
        
        <PanelToggleButton
          side="right"
          isOpen={state.customerQueueOpen}
          onToggle={handleToggleQueue}
          panelWidth={customerQueueWidth}
          isLargeScreen={isLargeScreen}
          touchFeedback={touchFeedback === 'queue'}
        />
      </main>
    </div>
  );
};

export default GameScreen;
